/**
 * Step three of the campaign wizard: who this goes to.
 *
 * The list here is marketing's own address book, not the Sales contact list.
 * They answer different questions — a sales contact is somebody a deal is being
 * worked with, a marketing contact is only an address to reach — and a bought
 * list imported for one send used to land in the pipeline and bury the sales
 * team's own work. Anything added or imported from this step is written to
 * Marketing → Contacts and stays there.
 *
 * A campaign can still carry a saved segment: `campaignRecipients` resolves
 * both, so anything created before keeps working.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { channelLabel } from '../../constants/marketing.js';
import { reachSummary, campaignRecipients, unreachableBecause, reasonLabel } from '../../domain/marketing.js';
import { describePhone, digitsOnly, storedPhoneProblem } from '../../lib/phone.js';
import PhoneField from '../PhoneField.jsx';
import ImportContacts from './ImportContacts.jsx';

const BLANK = { name: '', email: '', phone: '', company: '' };

export default function CampaignAudience({ channel, audienceId, contacts, onContacts }) {
  const { mkt, mktCtx, saveMktContact, importMktContacts, toast } = useApp();

  const [term, setTerm] = useState('');
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [draft, setDraft] = useState(BLANK);

  const book = mkt.contacts || [];
  const chosen = new Set(contacts || []);
  const q = term.trim().toLowerCase();
  const shown = book.filter(c => !q
    || [c.name, c.email, c.phone, c.company].some(v => String(v || '').toLowerCase().includes(q)));

  const everyone = campaignRecipients({ audience: audienceId, contacts }, mktCtx);
  const { ok, notes } = reachSummary(everyone, channel, mktCtx);

  /*
    * Ticking somebody a campaign cannot reach is the confusing case: the row
    * looked fine and the count did not move. Saying so on the row itself is
    * what stops the reach figure reading as a bug.
    */
  const blocked = c => unreachableBecause(
    { kind: 'mktContact', id: c.id, name: c.name, email: c.email, phone: c.phone },
    channel,
    mktCtx
  );

  /* A WhatsApp or SMS list is a list of numbers; an email list, of addresses.
     The row shows the one that matters for where this is going. */
  const byPhone = channel === 'whatsapp' || channel === 'sms';

  const toggle = cid => onContacts(chosen.has(cid)
    ? contacts.filter(x => x !== cid)
    : [...contacts, cid]);

  /* Search then "select all" is how a long list gets picked in bulk. */
  const takeShown = () => onContacts([...new Set([...contacts, ...shown.map(c => c.id)])]);
  const dropShown = () => onContacts(contacts.filter(cid => !shown.some(c => c.id === cid)));

  /* A contact added from here is for this send: a WhatsApp or SMS campaign
     needs a number that can be dialled, an email campaign an address. */
  const addOne = () => {
    if (!String(draft.name).trim()) { toast('Name the contact'); return; }
    const email = String(draft.email || '').trim();
    const phone = digitsOnly(draft.phone);
    if (byPhone && !phone) { toast('Type the phone number — that is where this campaign goes'); return; }
    if (!byPhone && !email) { toast('Type the email address — that is where this campaign goes'); return; }
    if (phone) {
      const why = storedPhoneProblem(phone);
      if (why) { toast(why); return; }
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast('That is not an email address'); return; }
    const twin = book.find(c => (phone && digitsOnly(c.phone) === phone)
      || (email && String(c.email || '').trim().toLowerCase() === email.toLowerCase()));
    if (twin) { toast(`${twin.name} is already on the list with that ${phone && digitsOnly(twin.phone) === phone ? 'number' : 'address'}`); return; }
    const cid = saveMktContact(null, {
      status: 'Subscribed', source: 'Campaign', notes: '',
      ...draft, name: draft.name.trim(), email, phone
    });
    if (cid) onContacts([...contacts, cid]);
    setDraft(BLANK);
    setAdding(false);
  };

  return (
    <>
      <div className="crow">
        <input
          className="csearch"
          value={term}
          placeholder={byPhone ? 'Search contacts by name or number…' : 'Search contacts by name, email or phone…'}
          onChange={e => setTerm(e.target.value)}
        />
        <button type="button" className="btn sm" onClick={takeShown}>Select all</button>
        <button type="button" className="btn sm" onClick={dropShown}>Clear</button>
      </div>

      <div className="crow">
        <button type="button" className="btn sm" onClick={() => setAdding(a => !a)}>
          <Icon name="plus" />New contact
        </button>
        <button
          type="button"
          className={`btn sm${importing ? ' primary' : ''}`}
          onClick={() => setImporting(v => !v)}
        >
          <Icon name="up" />Import
        </button>
        <span className="chint">
          Every address is checked before it lands. Saved to Marketing &rarr; Contacts.
        </span>
      </div>

      {/* Imported contacts are selected as well — picking them again by hand
          is the whole thing you were trying to avoid. */}
      {importing ? (
        <ImportContacts
          channel={channel}
          onClose={() => setImporting(false)}
          onImported={ids => onContacts([...new Set([...contacts, ...ids])])}
        />
      ) : null}

      {adding ? (
        <div className="cadd">
          <input
            autoFocus
            value={draft.name}
            placeholder="Name"
            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          />
          <input
            value={draft.email}
            placeholder="Email"
            onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
          />
          <PhoneField value={draft.phone} onChange={v => setDraft(d => ({ ...d, phone: v }))} />
          <input
            value={draft.company}
            placeholder="Company"
            onChange={e => setDraft(d => ({ ...d, company: e.target.value }))}
          />
          <button type="button" className="btn sm primary" onClick={addOne}>Add</button>
        </div>
      ) : null}

      {shown.length ? (
        <div className="clist">
          {shown.map(c => {
            const why = blocked(c);
            const ph = byPhone ? describePhone(c.phone) : null;
            return (
              <label className={`cpick${chosen.has(c.id) ? ' on' : ''}`} key={c.id}>
                <input type="checkbox" className="chk" checked={chosen.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="cpn">
                  <b>{c.name}</b>
                  {byPhone ? (
                    <small className="cpphone">
                      {ph.digits
                        ? <>{ph.flag ? <span className="cpflag">{ph.flag}</span> : null}{ph.text}</>
                        : 'no phone number'}
                    </small>
                  ) : (
                    <small>{c.email || 'no email address'}</small>
                  )}
                </span>
                {why
                  ? <span className="cps out">{reasonLabel(why)}</span>
                  : c.status ? <span className="cps">{c.status}</span> : null}
              </label>
            );
          })}
        </div>
      ) : (
        <p className="muted">
          {book.length
            ? 'No contact matches that search.'
            : (
              <>
                No marketing contacts yet — add one or import a list here, or manage the whole
                list under <Link to="/marketing/contacts">Marketing &rarr; Contacts</Link>.
              </>
            )}
        </p>
      )}

      <div className="reachbox">
        <div className="rnum">{ok.length}</div>
        <div>
          <b>reachable on {channelLabel(channel)}</b>
          {/* Each reason is named, because each one is fixed somewhere
              different — a status on the contact, the opt-out list, or the
              address itself. */}
          <div className="muted">
            {everyone.length ? `${everyone.length} chosen` : 'nobody chosen yet'}
            {notes.length
              ? ` · skipped: ${notes.join(', ')}`
              : everyone.length ? ' · everybody can be reached' : ''}
          </div>
        </div>
      </div>
    </>
  );
}
